import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { ReactElement, forwardRef, useRef } from 'react';
import { WidgetType } from 'types/widget.types';
import { ReadOnlyRenderType } from 'widgets/Widget.types';
import {
  ReadOnlyWidgetBase,
  TransparentImg,
  WidgetBase,
  WidgetBaseProp,
  WidgetSideMenuSettings,
  WidgetToolbar,
  useEditor,
  useWidget,
} from 'widgets/sdk';
import { InteractiveElementWrapper } from 'widgets/sdk/InteractiveElements';

import { useResponsiveHighcharts } from '../ChartWidget.hooks';
import { ChartWidgetToolbar } from '../ChartWidgetToolbar';
import { DataTableChartMenuSettings } from '../DataTableChartCommon/DataTableChartCommonMenuSettings';
import { HighChartsWrapper } from '../HighChartsWrapper';
import { StackedAreaChartWidgetData } from './StackedAreaChartWidget.types';

// Required so Marker Symbol is drawn at Legend same as Line charts
// https://www.highcharts.com/forum/viewtopic.php?t=41532
declare module 'highcharts/highcharts' {
  let seriesTypes: Highcharts.Dictionary<typeof Highcharts.Series>;
}
// @ts-ignore
Highcharts.seriesTypes.area.prototype.drawLegendSymbol = Highcharts.seriesTypes.line.prototype.drawLegendSymbol;

export const StackedAreaChartWidget = ({ getWidgetMemberComponent: getWidget }: WidgetBaseProp): ReactElement => {
  const { widthPx, heightPx } = useWidget();
  const { openWidgetSideMenu } = useEditor();

  const chartRef = useRef<HighchartsReact.RefObject>(null);

  const { customOnResize } = useResponsiveHighcharts({ chartRef, heightPx, widthPx });

  return (
    <WidgetBase onResize={customOnResize} onDoubleClick={openWidgetSideMenu}>
      <WidgetSideMenuSettings>
        <DataTableChartMenuSettings
          dataTableProps={{ colHeaders: true }}
          showAxisConfiguration
          showDataLabels
          showGridLinesConfiguration
          showLabelsConfiguration
          showNumberFormatSwitch
          showLegendsConfiguration
          showPatterns
          getWidget={getWidget}
        />
      </WidgetSideMenuSettings>
      <WidgetToolbar>
        <ChartWidgetToolbar />
      </WidgetToolbar>
      <StackedAreaChart ref={chartRef} />
    </WidgetBase>
  );
};

interface StackedAreaChartProps {
  waitForFonts?: boolean;
}

export const ReadOnlyStackedAreaChartWidget = ({ includeAltTextImg }: WidgetBaseProp): ReactElement => {
  const { renderType, interactions } = useWidget<StackedAreaChartWidgetData>();
  const waitForFonts = renderType !== ReadOnlyRenderType.ThumbnailReadOnly;
  const isPreview = renderType === ReadOnlyRenderType.StructureTreeList;

  return (
    <ReadOnlyWidgetBase>
      <InteractiveElementWrapper interactions={isPreview ? interactions : undefined}>
        <StackedAreaChart waitForFonts={waitForFonts} />
      </InteractiveElementWrapper>
      {includeAltTextImg && <TransparentImg />}
    </ReadOnlyWidgetBase>
  );
};

const StackedAreaChart = forwardRef<HighchartsReact.RefObject, StackedAreaChartProps>(
  ({ waitForFonts = true }, chartRef) => {
    return (
      <HighChartsWrapper chartRef={chartRef} chartType={WidgetType.StackedAreaChart} waitForFonts={waitForFonts} />
    );
  },
);
StackedAreaChart.displayName = 'StackedAreaChart';
